import { useState } from 'react'
import { Loader2, Printer } from 'lucide-react'
import { toast } from 'sonner'
import type { MouseEvent } from 'react'

import type { StockInNotePrintData } from '@/lib/stock-in-note-print'
import type { BagsDialogRow } from '@/components/warehouse/stock-in-note-bags-dialog'
import { Button } from '@/components/ui/button'
import { StockInNoteBagsDialog } from '@/components/warehouse/stock-in-note-bags-dialog'
import { isKgUnit } from '@/lib/stock-in-note-print'
import { captureStockInNoteBags } from '@/lib/stock-in-note-bags-tracking'
import { useStockInNotePrint } from '@/hooks/use-stock-in-note-print'
import { m } from '@/lib/paraglide/messages'

interface Props<TNote> {
    /** Nguồn phiếu: trả thẳng khi màn hình đã có sẵn (màn chi tiết), hoặc trả
     *  Promise khi phải tải trước (dòng danh sách — API danh sách không trả
     *  dòng hàng). `null` nghĩa là không lấy được phiếu. */
    loadNote: () => TNote | null | Promise<TNote | null>
    toPrintData: (note: TNote) => Omit<StockInNotePrintData, 'company'>
    /** Chỉ hiện biểu tượng — dùng trong ô thao tác của bảng danh sách. */
    iconOnly?: boolean
    /** M-196: phiếu có dòng tính theo kg thì hỏi số bao từng dòng trước khi
     *  in, in kèm cột "Số bao". Không bật = in ngay như cũ. */
    askBagsForKg?: boolean
}

type PrintData = Omit<StockInNotePrintData, 'company'>

/** Nút in phiếu nhập kho, dùng chung cho màn chi tiết và dòng danh sách của cả
 *  6 kho. Chặn lan sự kiện để bấm nút trong dòng bảng không mở màn chi tiết. */
export function StockInNotePrintButton<TNote>({
    loadNote,
    toPrintData,
    iconOnly = false,
    askBagsForKg = false,
}: Props<TNote>) {
    const { print } = useStockInNotePrint()
    const [loading, setLoading] = useState(false)
    // Phiếu đang chờ người in nhập số bao (hộp thoại mở khi khác null).
    const [awaitingBags, setAwaitingBags] = useState<{
        data: PrintData
        rows: BagsDialogRow[]
    } | null>(null)

    const handleClick = async (event: MouseEvent) => {
        event.stopPropagation()
        if (loading) return
        setLoading(true)
        try {
            const note = await loadNote()
            if (!note) {
                toast.error(m.sinp_load_note_failed())
                return
            }
            const data = toPrintData(note)
            const kgRows = askBagsForKg
                ? data.rows.flatMap((row, index) =>
                      isKgUnit(row.unit) ? [{ index, row }] : [],
                  )
                : []
            if (kgRows.length > 0) {
                setAwaitingBags({ data, rows: kgRows })
                captureStockInNoteBags({
                    stage: 'prompted',
                    kgRowCount: kgRows.length,
                    rowCount: data.rows.length,
                })
                return
            }
            print(data)
        } catch {
            toast.error(m.sinp_load_note_failed())
        } finally {
            setLoading(false)
        }
    }

    const cancelBags = () => {
        if (awaitingBags) {
            captureStockInNoteBags({
                stage: 'cancelled',
                kgRowCount: awaitingBags.rows.length,
                rowCount: awaitingBags.data.rows.length,
            })
        }
        setAwaitingBags(null)
    }

    const printWithBags = (bags: Array<number | undefined>) => {
        if (!awaitingBags) return
        const { data, rows } = awaitingBags
        const byIndex = new Map(rows.map((r, i) => [r.index, bags[i]]))
        setAwaitingBags(null)
        captureStockInNoteBags({
            stage: 'printed',
            kgRowCount: rows.length,
            rowCount: data.rows.length,
            filledRowCount: bags.filter((b) => b !== undefined).length,
        })
        print({
            ...data,
            // Để trống hết thì in như phiếu thường, không thêm cột rỗng.
            showBags: bags.some((b) => b !== undefined),
            rows: data.rows.map((row, index) =>
                byIndex.has(index) ? { ...row, bags: byIndex.get(index) } : row,
            ),
        })
    }

    return (
        // Hộp thoại render qua portal nhưng sự kiện React vẫn nổi lên cây cha:
        // nút nằm trong dòng bảng danh sách (bấm dòng = mở chi tiết), nên chặn
        // ở đây để bấm trong hộp thoại không mở màn chi tiết.
        <span className="contents" onClick={(e) => e.stopPropagation()}>
            <Button
                type="button"
                variant={iconOnly ? 'ghost' : 'outline'}
                size={iconOnly ? 'icon' : 'default'}
                className={iconOnly ? undefined : 'h-11 md:h-9'}
                disabled={loading}
                aria-label={m.sinp_print_btn()}
                title={m.sinp_print_btn()}
                onClick={(event) => void handleClick(event)}
            >
                {loading ? (
                    <Loader2 className="size-4 animate-spin" />
                ) : (
                    <Printer className="size-4" />
                )}
                {!iconOnly && (
                    <span className="ml-1">{m.sinp_print_btn()}</span>
                )}
            </Button>
            <StockInNoteBagsDialog
                rows={awaitingBags?.rows ?? null}
                onCancel={cancelBags}
                onConfirm={printWithBags}
            />
        </span>
    )
}
